import { FC, useMemo, useEffect } from "react";
import { AssessmentViewProps } from "./assessment.interfaces";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import { Grid, Box, CircularProgress, Alert, Tabs, Tab } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import MainTitle from "../../components/mainTitle";
import { useInstituteContext } from "../../../context/institute.context";
import { pages } from "../../../utils/common";
import { useClassHook, useSubjectsHook } from "../../../hooks/useAcademicHook";
import { hasAssessmentPrerequisites } from "./assessment.helpers";
import ScoringStructureView from "./scoringStructure.view";
import GradeCriteriaView from "./gradeCriteria.view";
import EvaluationFrameworkView from "./evaluationFramework.view";
import ReportCardTemplatesView from "./reportCardTemplates.view";
import { AssessmentInstituteRequiredEmptyState } from "./assessmentInstituteRequiredEmptyState";
import AcademicAssessmentsTab from "./academicAssessmentsTab.view";

const ASSESSMENT_INSTITUTE_GATE_SECTIONS = [
  "class assessments",
  "scoring structure",
  "grade criteria",
  "evaluation framework",
  "report card templates",
] as const;

const ASSESSMENT_TAB_SLUGS = [
  "assessments",
  "scoring-structure",
  "grade-criteria",
  "evaluation-framework",
  "report-card-templates",
] as const;

type AssessmentTabSlug = (typeof ASSESSMENT_TAB_SLUGS)[number];

const isAssessmentTabSlug = (value: string): value is AssessmentTabSlug =>
  (ASSESSMENT_TAB_SLUGS as readonly string[]).includes(value);

const AssessmentView: FC<AssessmentViewProps> = () => {
  const { selectedInstitute } = useInstituteContext();
  const navigate = useNavigate();
  const { tab } = useParams<{ tab: string }>();
  const activeTab = useMemo(() => {
    if (tab && isAssessmentTabSlug(tab)) {
      return ASSESSMENT_TAB_SLUGS.indexOf(tab);
    }
    return ASSESSMENT_TAB_SLUGS.indexOf("assessments");
  }, [tab]);

  const activeSlug = ASSESSMENT_TAB_SLUGS[activeTab];

  const {
    data: classes,
    isLoading: classesLoading,
    error: classesError,
  } = useClassHook();
  const {
    data: subjects,
    isLoading: subjectsLoading,
    error: subjectsError,
  } = useSubjectsHook();

  useEffect(() => {
    if (tab === "frameworks") {
      navigate(`${pages.assessment.path}/assessments`, { replace: true });
      return;
    }
    if (tab && !isAssessmentTabSlug(tab)) {
      navigate(`${pages.assessment.path}/assessments`, { replace: true });
    }
  }, [tab, navigate]);

  const prerequisitesMet = useMemo(
    () => hasAssessmentPrerequisites(classes, subjects),
    [classes, subjects],
  );
  const isLoadingPrerequisites = classesLoading || subjectsLoading;
  const hasPrerequisitesError = classesError || subjectsError;

  const tabBar = (
    <Box
      sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}
      data-testid="assessment-tabs-container"
    >
      <Tabs
        value={activeTab}
        onChange={(_, newValue) =>
          navigate(`${pages.assessment.path}/${ASSESSMENT_TAB_SLUGS[newValue]}`)
        }
        data-testid="assessment-tabs"
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Assessments" data-testid="academic-assessments-tab" />
        <Tab
          label="Scoring Structure"
          data-testid="scoring-structure-tab"
        />
        <Tab label="Grade Criteria" data-testid="grade-criteria-tab" />
        <Tab
          label="Evaluation Framework"
          data-testid="evaluation-framework-tab"
        />
        <Tab
          label="Report card templates"
          data-testid="report-card-templates-tab"
        />
      </Tabs>
    </Box>
  );

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.assessment} />
        <Grid container spacing={3} data-testid="assessment-view" sx={{ p: 3 }}>
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
              data-testid="assessment-header"
            >
              <MainTitle title="Assessment & Scoring" />
            </Box>
            {tabBar}
          </Grid>
          <Grid item xs={12}>
            <AssessmentInstituteRequiredEmptyState
              sectionLabel={ASSESSMENT_INSTITUTE_GATE_SECTIONS[activeTab]}
              data-testid="assessment-no-institute"
            />
          </Grid>
        </Grid>
      </>
    );
  }

  if (activeSlug !== "assessments" && hasPrerequisitesError) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.assessment} />
        <Box p={3} data-testid="assessment-view">
          <MainTitle title="Assessment & Scoring" />
          <Alert severity="error" data-testid="assessment-prerequisites-error">
            Failed to load classes or subjects data. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  if (activeSlug !== "assessments" && isLoadingPrerequisites) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.assessment} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="assessment-view"
        >
          <CircularProgress data-testid="assessment-loading-spinner" />
        </Box>
      </>
    );
  }

  const showPrerequisitesEmptyState =
    !prerequisitesMet &&
    activeSlug !== "assessments" &&
    activeSlug !== "report-card-templates";

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.assessment} />

      <Grid container spacing={3} data-testid="assessment-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
            data-testid="assessment-header"
          >
            <MainTitle title="Assessment & Scoring" />
          </Box>
          {tabBar}
        </Grid>

        {activeSlug === "assessments" && (
          <AcademicAssessmentsTab isActive={activeSlug === "assessments"} />
        )}

        {showPrerequisitesEmptyState ? (
          <Grid item xs={12}>
            <Alert severity="info" data-testid="assessment-prerequisites-alert">
              Set up classes and subjects in Academics to use scoring,
              grade criteria, and evaluation framework.
            </Alert>
          </Grid>
        ) : (
          <>
            {activeTab === 1 && (
              <Grid item xs={12} data-testid="scoring-structure-tab-content">
                <ScoringStructureView />
              </Grid>
            )}

            {activeTab === 2 && (
              <Grid item xs={12} data-testid="grade-criteria-tab-content">
                <GradeCriteriaView />
              </Grid>
            )}

            {activeTab === 3 && (
              <Grid item xs={12} data-testid="evaluation-framework-tab-content">
                <EvaluationFrameworkView />
              </Grid>
            )}

            {activeTab === 4 && (
              <Grid item xs={12} data-testid="report-card-templates-tab-content">
                <ReportCardTemplatesView />
              </Grid>
            )}
          </>
        )}
      </Grid>
    </>
  );
};

export default AssessmentView;
